import { useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { download, ExportButton, fileName, ledgerSheet, rangeLabel, tableSheet } from "./excel";

const SALESMAN_COLUMNS = [
  { key: "salesPersonName", label: "Salesman" },
  { key: "totalCollected", label: "Collected", format: "money" },
  { key: "totalDeposited", label: "Deposited", format: "money" },
  { key: "netCash", label: "Net Cash Held", format: "money" },
];
const BANK_COLUMNS = [
  { key: "bankAccountName", label: "Bank Account" },
  { key: "totalDeposited", label: "Total Deposited", format: "money" },
];

const money = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const TABS = [
  { key: "ledger", label: "Ledger" },
  { key: "salesman", label: "Salesman-wise" },
  { key: "bank", label: "Bank-wise" },
];

const CashInHandReport = () => {
  const [tab, setTab] = useState("ledger");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [ledger, setLedger] = useState(null);
  const [salesmanRows, setSalesmanRows] = useState([]);
  const [bankRows, setBankRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // per tab: the date range its data on screen was loaded with
  const [applied, setApplied] = useState({});

  const hasData = tab === "ledger" ? Boolean(ledger) : tab === "salesman" ? salesmanRows.length > 0 : bankRows.length > 0;

  const exportExcel = () => {
    const filters = rangeLabel(applied[tab]?.fromDate, applied[tab]?.toDate);
    const sheet =
      tab === "ledger"
        ? ledgerSheet("Cash in Hand", filters, ledger, "Cash in Hand")
        : tab === "salesman"
        ? tableSheet("Cash in Hand — Salesman-wise", filters, SALESMAN_COLUMNS, salesmanRows, "Salesman-wise")
        : tableSheet("Cash in Hand — Bank-wise", filters, BANK_COLUMNS, bankRows, "Bank-wise");
    download(fileName(`Cash in Hand ${TABS.find((t) => t.key === tab).label}`, filters), [sheet]);
  };

  const load = async (activeTab) => {
    setLoading(true);
    try {
      if (activeTab === "ledger") {
        const data = await api.get("/api/v1/reports/cash-in-hand", { fromDate: fromDate || undefined, toDate: toDate || undefined });
        setLedger(data);
      } else if (activeTab === "salesman") {
        const data = await api.get("/api/v1/reports/cash-in-hand/salesman-wise", { fromDate: fromDate || undefined, toDate: toDate || undefined });
        setSalesmanRows(Array.isArray(data) ? data : []);
      } else {
        const data = await api.get("/api/v1/reports/cash-in-hand/bank-wise", { fromDate: fromDate || undefined, toDate: toDate || undefined });
        setBankRows(Array.isArray(data) ? data : []);
      }
      setApplied((prev) => ({ ...prev, [activeTab]: { fromDate, toDate } }));
    } catch (error) {
      toast.error(error.message || "Failed to load cash in hand report");
    } finally {
      setLoading(false);
    }
  };

  const rows = ledger?.entries || [];

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading cash in hand...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Cash collected from customers (debit) against cash deposited into a bank (credit) — showing what's currently
        on hand. Only cash-mode receipts count; bank/UPI/cheque receipts never touch physical cash.
      </div>

      <div className="filters-bar">
        {TABS.map((t) => (
          <button key={t.key} className={tab === t.key ? "primary" : "secondary"} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
        <input type="date" onClick={(e) => e.target.showPicker?.()} value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" onClick={(e) => e.target.showPicker?.()} value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <button className="primary" onClick={() => load(tab)} disabled={loading}>
          {loading ? "Loading..." : "Load"}
        </button>
        <ExportButton onClick={exportExcel} disabled={loading || !hasData} />
      </div>

      {tab === "ledger" && ledger ? (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Particulars</th>
                <th>Vch Type</th>
                <th>Vch No</th>
                <th>Debit</th>
                <th>Credit</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <span className="empty-state-title">No cash transactions in this period</span>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={index}>
                    <td>{row.date}</td>
                    <td>{row.particulars}</td>
                    <td>{row.vchType}</td>
                    <td>{row.vchNo}</td>
                    <td>{row.debit && Number(row.debit) !== 0 ? money(row.debit) : ""}</td>
                    <td>{row.credit && Number(row.credit) !== 0 ? money(row.credit) : ""}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={{ textAlign: "right", fontWeight: 600 }}>
                  Opening Balance:
                </td>
                <td colSpan={2} style={{ fontWeight: 600 }}>{money(ledger.openingBalance)}</td>
              </tr>
              <tr>
                <td colSpan={4} style={{ textAlign: "right", fontWeight: 600 }}>
                  Current Total:
                </td>
                <td style={{ fontWeight: 600 }}>{money(ledger.totalDebit)}</td>
                <td style={{ fontWeight: 600 }}>{money(ledger.totalCredit)}</td>
              </tr>
              <tr>
                <td colSpan={4} style={{ textAlign: "right", fontWeight: 700 }}>
                  Cash in Hand:
                </td>
                <td colSpan={2} style={{ fontWeight: 700 }}>{money(ledger.closingBalance)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : null}

      {tab === "salesman" ? (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Salesman</th>
                <th>Collected</th>
                <th>Deposited</th>
                <th>Net Cash Held</th>
              </tr>
            </thead>
            <tbody>
              {salesmanRows.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <div className="empty-state">
                      <span className="empty-state-title">No cash activity in this period</span>
                    </div>
                  </td>
                </tr>
              ) : (
                salesmanRows.map((row, index) => (
                  <tr key={index}>
                    <td>{row.salesPersonName}</td>
                    <td>{money(row.totalCollected)}</td>
                    <td>{money(row.totalDeposited)}</td>
                    <td>{money(row.netCash)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "bank" ? (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Bank Account</th>
                <th>Total Deposited</th>
              </tr>
            </thead>
            <tbody>
              {bankRows.length === 0 ? (
                <tr>
                  <td colSpan={2}>
                    <div className="empty-state">
                      <span className="empty-state-title">No deposits in this period</span>
                    </div>
                  </td>
                </tr>
              ) : (
                bankRows.map((row, index) => (
                  <tr key={index}>
                    <td>{row.bankAccountName}</td>
                    <td>{money(row.totalDeposited)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default CashInHandReport;
